import React, { useState, useMemo, useRef } from 'react';
import { RoadmapProject, QuarterInfo } from '../types';
import {
  getAllQuarters,
  getCurrentQuarterInfo,
  getProjectQuarterInfo,
  getTimelineBarClass,
  getStatusLabel,
} from '../utils/roadmapUtils';

interface TimelineViewProps {
  projects: RoadmapProject[];
  onProjectClick: (gid: string) => void;
}

interface TooltipState {
  project: RoadmapProject;
  x: number;
  y: number;
}

/** Build a map: programName → quarterKey → projects[] */
const buildTimelineData = (
  projects: RoadmapProject[],
  quarters: QuarterInfo[]
): Map<string, Map<string, RoadmapProject[]>> => {
  const programMap = new Map<string, Map<string, RoadmapProject[]>>();

  // Preserve insertion order (sorted by program name)
  const programNames = Array.from(new Set(projects.map(p => p.program || 'Unassigned'))).sort();
  programNames.forEach(name => programMap.set(name, new Map()));

  // Seed all quarter keys so every program has every column
  programMap.forEach(qMap => {
    quarters.forEach(q => qMap.set(q.key, []));
  });

  projects.forEach(p => {
    const program = p.program || 'Unassigned';
    const qi = getProjectQuarterInfo(p);
    const quarterKey = qi?.key ?? '__no_date__';

    if (!programMap.has(program)) {
      const qMap = new Map<string, RoadmapProject[]>();
      quarters.forEach(q => qMap.set(q.key, []));
      programMap.set(program, qMap);
    }

    const qMap = programMap.get(program)!;
    if (!qMap.has(quarterKey)) qMap.set(quarterKey, []);
    qMap.get(quarterKey)!.push(p);
  });

  return programMap;
};

export const TimelineView: React.FC<TimelineViewProps> = ({ projects, onProjectClick }) => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const quarters = useMemo(() => {
    const qs = getAllQuarters(projects);
    // Always show at least the current quarter
    const current = getCurrentQuarterInfo();
    if (!qs.find(q => q.key === current.key)) {
      qs.push(current);
      qs.sort((a, b) => a.sortKey - b.sortKey);
    }
    return qs;
  }, [projects]);

  const currentQuarterKey = useMemo(() => getCurrentQuarterInfo().key, []);

  const timelineData = useMemo(
    () => buildTimelineData(projects, quarters),
    [projects, quarters]
  );

  const programNames = useMemo(() => Array.from(timelineData.keys()), [timelineData]);

  // Grid template: sticky program label column + one column per quarter
  const gridTemplateColumns = `200px repeat(${quarters.length}, minmax(160px, 1fr))`;

  const handleBarMouseEnter = (e: React.MouseEvent, project: RoadmapProject) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTooltip({
      project,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
  };

  const handleBarMouseLeave = () => setTooltip(null);

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (projects.length === 0) {
    return (
      <div className="timeline-view">
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <h3>No projects to display</h3>
          <p>Try adjusting your filters or search query.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="timeline-view" ref={containerRef}>
      <div className="timeline-scroll-container">
        <div
          className="timeline-grid"
          style={{ gridTemplateColumns }}
        >
          {/* ── Header row ── */}
          <div className="timeline-program-label-header">Program</div>
          {quarters.map(q => (
            <div
              key={q.key}
              className={`timeline-quarter-header${q.key === currentQuarterKey ? ' is-current' : ''}`}
            >
              {q.key}
              {q.key === currentQuarterKey && (
                <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}>▼ Today</span>
              )}
            </div>
          ))}

          {/* ── Data rows ── */}
          {programNames.map(programName => {
            const qMap = timelineData.get(programName)!;
            return (
              <React.Fragment key={programName}>
                {/* Sticky program label */}
                <div className="timeline-program-label" title={programName}>
                  {programName}
                </div>

                {/* Quarter cells */}
                {quarters.map(q => {
                  const cellProjects = qMap.get(q.key) ?? [];
                  return (
                    <div
                      key={q.key}
                      className={`timeline-cell${q.key === currentQuarterKey ? ' is-current-quarter' : ''}`}
                    >
                      {cellProjects.map(p => (
                        <button
                          key={p.gid}
                          className={`timeline-bar ${getTimelineBarClass(p.status)}`}
                          onClick={() => onProjectClick(p.gid)}
                          onMouseEnter={e => handleBarMouseEnter(e, p)}
                          onMouseLeave={handleBarMouseLeave}
                          type="button"
                          title={p.name}
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Floating tooltip */}
      {tooltip && (
        <div
          className="timeline-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
          }}
          aria-hidden="true"
        >
          <div className="timeline-tooltip-name">{tooltip.project.name}</div>
          <div className="timeline-tooltip-meta">
            <span>{getStatusLabel(tooltip.project.status)}</span>
            {tooltip.project.launch_date && (
              <span>Launch: {formatDate(tooltip.project.launch_date)}</span>
            )}
            {tooltip.project.assignee && (
              <span>Owner: {tooltip.project.assignee.name}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineView;